import { useState, useRef, useEffect } from 'react'
import { UserItem } from '../../api/tasks'
import { Bold, Italic, List, ListOrdered, Code, Quote, EyeOff, AtSign } from 'lucide-react'

interface Props {
  textareaRef: React.RefObject<HTMLTextAreaElement | null>
  value: string
  onChange: (val: string) => void
  users: UserItem[]
}

export default function MarkdownToolbar({ textareaRef, value, onChange, users }: Props) {
  const [showMentionDropdown, setShowMentionDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowMentionDropdown(false)
      }
    }
    if (showMentionDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMentionDropdown])

  const insertSyntax = (before: string, after: string = '', defaultPlaceholder: string = '') => {
    const textarea = textareaRef.current
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = value.substring(start, end)
    const content = selectedText || defaultPlaceholder
    const replacement = before + content + after
    
    const newValue = value.substring(0, start) + replacement + value.substring(end)
    onChange(newValue)

    // Refocus and restore cursor selection
    setTimeout(() => {
      textarea.focus()
      const newCursorStart = start + before.length
      const newCursorEnd = newCursorStart + content.length
      textarea.setSelectionRange(newCursorStart, newCursorEnd)
    }, 50)
  }

  const handleMention = (user: UserItem) => {
    insertSyntax(`@${user.name} `, '', '')
    setShowMentionDropdown(false)
  }

  return (
    <div className="flex flex-wrap items-center bg-gray-50 border border-b-0 border-gray-300 rounded-t-lg px-2.5 py-1.5 gap-1 select-none relative">
      <button
        type="button"
        onClick={() => insertSyntax('**', '**', 'жирный текст')}
        className="p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded transition"
        title="Жирный текст"
      >
        <Bold className="w-4 h-4" />
      </button>

      <button
        type="button"
        onClick={() => insertSyntax('*', '*', 'курсив')}
        className="p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded transition"
        title="Курсив"
      >
        <Italic className="w-4 h-4" />
      </button>

      <div className="w-px h-4 bg-gray-300 mx-1"></div>

      <button
        type="button"
        onClick={() => insertSyntax('\n- ', '', 'пункт списка')}
        className="p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded transition"
        title="Маркированный список"
      >
        <List className="w-4 h-4" />
      </button>

      <button
        type="button"
        onClick={() => insertSyntax('\n1. ', '', 'пункт списка')}
        className="p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded transition"
        title="Нумерованный список"
      >
        <ListOrdered className="w-4 h-4" />
      </button>

      <div className="w-px h-4 bg-gray-300 mx-1"></div>

      <button
        type="button"
        onClick={() => insertSyntax('\n```javascript\n', '\n```\n', 'код')}
        className="p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded transition"
        title="Блок кода"
      >
        <Code className="w-4 h-4" />
      </button>

      <button
        type="button"
        onClick={() => insertSyntax('\n> ', '', 'цитата')}
        className="p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded transition"
        title="Цитата"
      >
        <Quote className="w-4 h-4" />
      </button>

      <button
        type="button"
        onClick={() => insertSyntax('\n<details>\n<summary>Спойлер</summary>\n', '\n</details>\n', 'скрытый текст')}
        className="p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded transition"
        title="Спойлер"
      >
        <EyeOff className="w-4 h-4" />
      </button>

      <div className="w-px h-4 bg-gray-300 mx-1"></div>

      {/* Mention User Button */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setShowMentionDropdown(!showMentionDropdown)}
          className={`p-1 rounded transition flex items-center gap-0.5 ${
            showMentionDropdown 
              ? 'bg-blue-100 text-blue-700' 
              : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200'
          }`}
          title="Упомянуть пользователя"
        >
          <AtSign className="w-4 h-4" />
          <span className="text-[10px] font-bold">@</span>
        </button>

        {showMentionDropdown && (
          <div className="absolute left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto py-1">
            <div className="text-[10px] text-gray-400 font-bold px-2 py-1 uppercase tracking-wider">
              Упомянуть:
            </div>
            {users.length > 0 ? (
              users.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => handleMention(user)}
                  className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-blue-50 transition"
                >
                  <span className="font-semibold block">{user.name}</span>
                  <span className="text-[9px] text-gray-400 block">{user.email}</span>
                </button>
              ))
            ) : (
              <div className="text-xs text-gray-400 px-3 py-1.5 italic">Нет пользователей</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
